import { useEffect, useState } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress, Typography, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { confirmOrder, type OrderListItem, type CreditCheckFailed, type ProductionNeed, type BomFeasibilityCheckFailed } from '../../api/comenzi'
import { errorMessage } from '../../api/client'

export default function ConfirmOrderDialog({ order, onClose }: { order: OrderListItem | null; onClose: () => void }) {
  const open = order !== null
  const qc = useQueryClient()
  const [creditFailure, setCreditFailure] = useState<CreditCheckFailed | null>(null)
  const [bomFailure, setBomFailure] = useState<BomFeasibilityCheckFailed | null>(null)
  const [reason, setReason] = useState('')
  const [bomReason, setBomReason] = useState('')
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)
  const [productionNeeds, setProductionNeeds] = useState<ProductionNeed[] | null>(null)

  useEffect(() => {
    if (open) {
      setCreditFailure(null); setBomFailure(null); setReason(''); setBomReason('')
      setPendingMessage(null); setProductionNeeds(null)
    }
  }, [open])

  const mutation = useMutation({
    // Cele două verificări rulează secvențial în backend (credit, apoi fezabilitate BOM) —
    // dacă ambele au eșuat pe rând, motivele deja introduse se trimit împreună la a doua
    // reîncercare, ca utilizatorul să nu reintroducă motivul de credit deja acceptat.
    mutationFn: () => confirmOrder(order!.id, reason || undefined, bomReason || undefined),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'comenzi'] })
      if (r.pendingApproval) setPendingMessage('Comanda a fost trimisă la aprobare — va fi confirmată automat după decizie.')
      else if (r.productionNeeds.length > 0) setProductionNeeds(r.productionNeeds)
      else onClose()
    },
    onError: (e) => {
      if (axios.isAxiosError(e) && e.response?.status === 409 && e.response.data?.report) {
        setBomFailure(e.response.data as BomFeasibilityCheckFailed)
        setCreditFailure(null)
      } else if (axios.isAxiosError(e) && e.response?.status === 409 && e.response.data?.outstanding !== undefined) {
        setCreditFailure(e.response.data as CreditCheckFailed)
        setBomFailure(null)
      } else {
        setCreditFailure(null); setBomFailure(null)
      }
    },
  })

  const genericError = mutation.isError && !creditFailure && !bomFailure ? errorMessage(mutation.error) : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Confirmare comandă {order?.orderNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {productionNeeds ? (
            <>
              <Alert severity="info">
                Comanda a fost confirmată. Stocul nu acoperă integral cantitatea comandată — necesar de producție (FR-CMD-005):
              </Alert>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Articol</TableCell><TableCell align="right">Comandat</TableCell><TableCell align="right">Disponibil</TableCell><TableCell align="right">Lipsă</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {productionNeeds.map((n) => (
                    <TableRow key={n.articleId}>
                      <TableCell>{n.articleCode} — {n.articleDescription}</TableCell>
                      <TableCell align="right">{n.orderedQuantity}</TableCell>
                      <TableCell align="right">{n.availableQuantity}</TableCell>
                      <TableCell align="right">{n.shortfallQuantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary">
                Creați un ordin de producție din modulul Producție pentru cantitatea lipsă.
              </Typography>
            </>
          ) : pendingMessage ? (
            <Alert severity="info">{pendingMessage}</Alert>
          ) : creditFailure ? (
            <>
              <Alert severity="warning">
                Limita de credit a clientului ar fi depășită: sold {creditFailure.outstanding.toFixed(2).replace('.', ',')} peste limita de {creditFailure.creditLimit.toFixed(2).replace('.', ',')}
                {' '}(comanda curentă: {creditFailure.orderTotal.toFixed(2).replace('.', ',')}).
              </Alert>
              <TextField label="Motiv suprascriere limită de credit" fullWidth multiline minRows={2}
                value={reason} onChange={(e) => setReason(e.target.value)} />
              {genericError && <Alert severity="error">{genericError}</Alert>}
            </>
          ) : bomFailure ? (
            <>
              <Alert severity="warning">{bomFailure.message}</Alert>
              {bomFailure.report.criticalComponentCode && (
                <Alert severity="info">
                  Constrângerea critică: {bomFailure.report.criticalComponentCode}
                  {bomFailure.report.criticalComponentEstimatedDate && ` — disponibil estimat ${bomFailure.report.criticalComponentEstimatedDate}`}
                </Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Articol</TableCell><TableCell align="right">Necesar</TableCell><TableCell align="right">Disponibil</TableCell>
                    <TableCell align="right">Lipsă</TableCell><TableCell>Acțiune sugerată</TableCell><TableCell>Dată estimată</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bomFailure.report.lines.map((l) => (
                    <TableRow key={l.articleId} selected={l.articleId === bomFailure.report.criticalComponentArticleId}>
                      <TableCell>{l.articleCode} — {l.articleDescription}</TableCell>
                      <TableCell align="right">{l.requiredQuantity}</TableCell>
                      <TableCell align="right">{l.availableQuantity}</TableCell>
                      <TableCell align="right">{l.shortfallQuantity}</TableCell>
                      <TableCell>{l.suggestedAction}</TableCell>
                      <TableCell>{l.estimatedAvailabilityDate ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TextField label="Motiv suprascriere fezabilitate" fullWidth multiline minRows={2}
                value={bomReason} onChange={(e) => setBomReason(e.target.value)} />
              {genericError && <Alert severity="error">{genericError}</Alert>}
            </>
          ) : (
            <Typography>Confirmați această comandă?</Typography>
          )}
          {genericError && !creditFailure && !bomFailure && <Alert severity="error">{genericError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>{pendingMessage || productionNeeds ? 'Închide' : 'Renunță'}</Button>
        {!pendingMessage && !productionNeeds && (
          <Button variant="contained"
            disabled={mutation.isPending || (!!creditFailure && !reason.trim()) || (!!bomFailure && !bomReason.trim())}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>
            {creditFailure || bomFailure ? 'Confirmă cu suprascriere' : 'Confirmă'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
